/**
 * The campaign overview's "invitations send automatically ..." sentence,
 * built from the schedule actually stored.
 *
 * Reported 2026-09-30: an operator changed their wave to 09:00 and the
 * overview still said 10:00, because three strings hardcoded
 * "10:00 Mon–Fri" from the days when one cron did. The sentence has to
 * come from the same place the wave reads.
 *
 * While the schedule is loading — or when the times differ per day — the
 * caller gets `null` and shows a sentence with NO time in it. A vaguer
 * sentence is right; a confidently wrong hour is what caused this.
 */
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";

import { type WaveScheduleResponse, isPaused, summariseDays } from "./waveSchedule";

export function useWaveSummary(scope: { kind: "org" } | { kind: "campaign"; id: string }) {
  const { t, i18n } = useTranslation();
  const url =
    scope.kind === "org"
      ? ApiPaths.orgWaveSchedule
      : ApiPaths.campaignWaveSchedule(scope.id);

  // Same query key the editor uses, so opening Settings and coming back
  // costs nothing and the two can never disagree.
  const loaded = useQuery({
    queryKey: [
      "wave-schedule",
      scope.kind,
      scope.kind === "campaign" ? scope.id : "me",
    ],
    queryFn: async () => (await api.get<WaveScheduleResponse>(url)).data,
  });

  // Tolerate a payload that is not a schedule at all.
  //
  // This sentence is decoration on a page whose job is something else, so
  // it must never be able to take that page down. A deploy skew — new
  // frontend, old backend without the endpoint — would otherwise have
  // the campaign overview throw on `data.schedule.days`, which is
  // precisely what happened when an E2E catch-all returned a campaign
  // object here.
  const days = loaded.data?.schedule?.days;
  if (!days || typeof days !== "object") return { schedule: null, zone: null };
  const zone = loaded.data?.timezone ?? null;

  if (isPaused(days)) {
    return { schedule: t("wave.summary.paused"), zone };
  }
  const parts = summariseDays(days, i18n.language);
  if (!parts) {
    // Times differ per day — point at the settings rather than build a
    // list in fifteen languages.
    return { schedule: t("wave.summary.custom"), zone };
  }
  if (parts.kind === "daily") {
    return { schedule: t("wave.summary.daily", { time: parts.time }), zone };
  }
  if (parts.kind === "weekdays") {
    return { schedule: t("wave.summary.weekdays", { time: parts.time }), zone };
  }
  return {
    schedule: t("wave.summary.some", {
      days: parts.dayNames,
      time: parts.time,
    }),
    zone,
  };
}
